import { Component } from '@theme/component';
import { buildCartSectionsUrl, fetchConfig, onAnimationEnd, isCartPanelOpen, syncCartPanelFromSource, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartAddAnimationCompleteEvent, CartErrorEvent, CartSectionsMorphedEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { morphSection } from '@theme/section-renderer';
import { parseCartItemCount, syncCartBubbleCount } from '@theme/cart-icon';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  /** True from the moment a success animation cycle restarts until its cleanup fires. */
  #animating = false;

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }

    this.#animating = false;
  }

  /**
   * Cancels any in-flight success animation cycle and clears its cleanup timers.
   * Called by ProductFormComponent when it resets button presentation outside the
   * normal animation flow (add error, variant update) so `#animating` can't get
   * stuck `true` and permanently block future replays.
   */
  cancelAnimation() {
    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
      this.#resetTimeouts = [];
    }

    this.#animating = false;
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
    this.refs.addToCartButton.setAttribute('aria-disabled', 'true');
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
    this.refs.addToCartButton.removeAttribute('aria-disabled');
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const { addToCartButton } = this.refs;
    if (addToCartButton.disabled) return;

    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    if (!productForm?.refs.variantId?.value?.trim()) return;

    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    if (this.refs.addToCartButton.dataset.puppet !== 'true' && !this.closest('product-form-component')) {
      this.animateAddToCart();
    }
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    if (this.dataset.addToCartAnimation !== 'true') return;

    // Re-entrancy guard: a second call landing while the CSS animation from a
    // prior call is still playing would remove/re-add `data-added` mid-flight,
    // cancelling the running animation. `onAnimationEnd` uses Promise.allSettled,
    // so a cancelled Animation.finished still "settles" the first call's await
    // early — cutting its animation short instead of skipping cleanly. Successive
    // *separate* clicks should still replay the animation (that's the fix for the
    // no-replay bug); only a call that lands mid-cycle of an already-running one
    // is skipped here.
    if (this.#animating) return;
    this.#animating = true;

    try {
      const { addToCartButton } = this.refs;

      // Initialize the array if it doesn't exist
      if (!this.#resetTimeouts) {
        this.#resetTimeouts = [];
      }

      // Clear all existing timeouts
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
      this.#resetTimeouts = [];

      // Force the CSS animation to replay every time, even if a previous cycle hasn't
      // cleared `data-added` yet. Re-assigning the same attribute value does not restart
      // a CSS animation, so the attribute must be removed and reflowed before re-adding it.
      addToCartButton.removeAttribute('data-added');
      void addToCartButton.offsetWidth;
      addToCartButton.dataset.added = 'true';

      // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
      await yieldToMainThread();
      await onAnimationEnd(addToCartButton);

      // Create new timeout and store it in the array
      const timeoutId = setTimeout(() => {
        addToCartButton.removeAttribute('data-added');
        this.#animating = false;

        // Remove this timeout from the array
        const index = this.#resetTimeouts.indexOf(timeoutId);
        if (index > -1) {
          this.#resetTimeouts.splice(index, 1);
        }
      }, 800);

      this.#resetTimeouts.push(timeoutId);
    } catch (error) {
      this.#animating = false;
      console.error(error);
    }
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  /** @type {number} */
  #variantCartQuantity = 0;

  /** @type {boolean} */
  #cartAddBlocked = false;

  /** @type {boolean} */
  #addToCartInProgress = false;

  /** @type {AbortController | null} */
  #addAbortController = null;

  /** @type {number} */
  #addRequestId = 0;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    // The server already rendered the correct disabled state for the initial variant
    // (blocks/add-to-cart.liquid sets `disabled` based on `variant.available`). Read it
    // back instead of assuming available, so an out-of-stock variant doesn't get its
    // button silently re-enabled on connect — read straight from the DOM rather than
    // through the child component's refs, which may not be populated yet on connect
    // (e.g. for a dynamically-inserted quick-add form, whose connectedCallback runs
    // before its child add-to-cart-component's per custom-element upgrade ordering).
    const initialButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('add-to-cart-component [ref="addToCartButton"]')
    );
    const initiallyAvailable = initialButton ? !initialButton.hasAttribute('disabled') : true;

    this.#syncAddToCartButtonState(initiallyAvailable);

    if (this.refs.variantId?.value?.trim()) {
      this.#fetchAndUpdateCartQuantity().then(() => this.#syncAddToCartButtonState(initiallyAvailable));
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    this.#variantCartQuantity = cartQty;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Resolves cart state after add, always preferring authoritative item_count from /cart.js.
   * @param {Record<string, unknown>} response
   * @param {Cart | null} [addedCart] Precomputed via #cartFromAddResponse, reused instead of recomputed.
   * @returns {Promise<Cart | null>}
   */
  async #resolveCartAfterAdd(response, addedCart = this.#cartFromAddResponse(response)) {
    let cart = addedCart;

    if (!Number.isFinite(Number(cart?.item_count))) {
      const fetched = await this.#fetchAndUpdateCartQuantity();
      if (fetched) {
        cart = { ...(cart ?? {}), item_count: fetched.item_count, items: fetched.items ?? cart?.items };
      } else if (Number.isFinite(Number(response.item_count))) {
        cart = { ...(cart ?? {}), item_count: Number(response.item_count) };
      }
    } else {
      this.#updateCartQuantityFromData(cart);
    }

    return cart;
  }

  /**
   * Keeps every cart bubble (header + mobile nav) aligned with the server count.
   * @param {Cart | null | undefined} cart
   */
  async #syncAllCartBubbles(cart) {
    const count = parseCartItemCount(cart?.item_count);
    if (count === null) return;

    await syncCartBubbleCount(count, false);
  }

  /**
   * Builds a minimal cart object from a cart/add.js response.
   * @param {Record<string, unknown>} response
   * @returns {Cart | null}
   */
  #cartFromAddResponse(response) {
    /** @type {{ items?: Array<{ variant_id: number, quantity: number }>, item_count?: number }} */
    const cart = {};

    const itemCount = Number(response.item_count);
    if (Number.isFinite(itemCount)) {
      cart.item_count = itemCount;
    }

    const items = response.items;
    if (Array.isArray(items) && items.length > 0) {
      cart.items = items;
      return cart;
    }

    if (typeof response.variant_id === 'number') {
      cart.items = [
        {
          variant_id: response.variant_id,
          quantity: typeof response.quantity === 'number' ? response.quantity : 0,
        },
      ];
      return cart;
    }

    return Object.keys(cart).length > 0 ? cart : null;
  }

  /**
   * @returns {boolean}
   */
  #cartAppearsEmpty() {
    const visibleCountEl = document.querySelector('.cart-bubble__text-count:not(.hidden)');
    const visibleCount = Number(visibleCountEl?.textContent);
    if (Number.isFinite(visibleCount) && visibleCount > 0) return false;

    return !document.querySelector('.cart-bubble:not(.visually-hidden)');
  }

  /**
   * Morphs cart section markup from a cart/add response (drawer + panel only).
   * @param {Record<string, string> | undefined} sections
   * @param {string[]} sectionIds
   * @param {boolean} [cartWasEmpty=false]
   */
  async #morphCartSections(sections, sectionIds, cartWasEmpty = false) {
    if (!sections) return;

    const morphGeneration = window.__eaCartMorphGen ?? 0;
    const mode = cartWasEmpty ? 'full' : 'hydration';

    await Promise.all(
      [...new Set(sectionIds)].map(async (sectionId) => {
        const html = sections[sectionId];
        if (!html) return;

        if ((window.__eaCartMorphGen ?? 0) !== morphGeneration) return;

        try {
          await morphSection(sectionId, html, { mode });
          if (isCartPanelOpen()) syncCartPanelFromSource();
        } catch (error) {
          console.error(error);
        }
      })
    );
  }

  /**
   * @param {boolean} isBusy
   */
  #setAddToCartBusy(isBusy) {
    this.#addToCartInProgress = isBusy;

    this.querySelectorAll('add-to-cart-component').forEach((container) => {
      if (!(container instanceof AddToCartComponent)) return;

      const button = container.refs.addToCartButton;
      if (!button) return;

      if (isBusy) {
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('aria-busy');
      }
    });
  }

  /**
   * @returns {Promise<void> | undefined} Resolves once the button's success
   * animation has finished playing (or immediately if animation is disabled).
   */
  #startAddToCartAnimation() {
    return this.refs.addToCartButtonContainer?.animateAddToCart?.();
  }

  #resetAddToCartAnimation() {
    this.querySelectorAll('add-to-cart-component').forEach((container) => {
      if (!(container instanceof AddToCartComponent)) return;

      container.cancelAnimation();

      const button = container.refs.addToCartButton;
      if (button) {
        this.#resetAddToCartButtonPresentation(button);
      }
    });
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    this.#cartAddBlocked = false;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }

    this.#syncAddToCartButtonState();
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (!this.refs.variantId?.value?.trim()) {
      this.#syncAddToCartButtonState();
      return;
    }

    if (this.#isAtCartQuantityLimit()) {
      this.#showMaxQuantityError();
      return;
    }

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      this.#startAddToCartAnimation();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled || this.#addToCartInProgress) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.#isAtCartQuantityLimit()) {
      this.#showMaxQuantityError();
      return;
    }

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        this.#showMaxQuantityError(validation.maxQuantity);
        return;
      }
    }

    const formData = new FormData(form);
    const resolvedVariantId = overrideVariantId || formData.get('id')?.toString().trim();

    if (!resolvedVariantId) {
      this.#syncAddToCartButtonState();
      return;
    }

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    if (cartItemComponentsSectionIds.length > 0) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
      formData.append('sections_url', buildCartSectionsUrl());
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    this.#addAbortController?.abort();
    this.#addAbortController = new AbortController();
    const requestId = ++this.#addRequestId;

    const animationPromise = this.#startAddToCartAnimation();
    this.#setAddToCartBusy(true);

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      signal: this.#addAbortController.signal,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (requestId !== this.#addRequestId) return;

        if (response.status) {
          this.#cartAddBlocked = true;
          this.#resetAddToCartAnimation();

          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          await this.#fetchAndUpdateCartQuantity();
          this.#syncAddToCartButtonState();

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          this.#cartAddBlocked = false;
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          const cartWasEmpty = this.#cartAppearsEmpty();

          // /cart/add.js usually doesn't include item_count, so #resolveCartAfterAdd
          // often has to fall back to a second /cart.js fetch to get an authoritative
          // count. Kick that off but don't block the drawer/panel opening and section
          // morph on it — everything they need (response.sections) is already here.
          const addedCart = this.#cartFromAddResponse(response);
          const cartPromise = this.#resolveCartAfterAdd(response, addedCart);

          this.dispatchEvent(
            new CartAddEvent(addedCart ?? undefined, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );

          // Fire once the button's success animation finishes so the cart panel
          // auto-open can sequence itself after it — doesn't block the morph/bubble
          // sync below, which stay eager per the earlier speed fix.
          Promise.resolve(animationPromise).then(() => {
            this.dispatchEvent(
              new CartAddAnimationCompleteEvent(id.toString(), {
                source: 'product-form-component',
                productId: this.dataset.productId,
              })
            );
          });

          await this.#morphCartSections(response.sections, cartItemComponentsSectionIds, cartWasEmpty);

          // Signal that cart panel content is now populated, independent of the
          // (often slower) authoritative-count fetch that CartUpdateEvent waits on
          // below — the cart panel's hydrating gate needs this fired promptly and
          // unconditionally to avoid leaving the checkout button unclickable.
          this.dispatchEvent(
            new CartSectionsMorphedEvent(id.toString(), { source: 'product-form-component' })
          );

          const cart = await cartPromise;
          this.#syncAddToCartButtonState();
          await this.#syncAllCartBubbles(cart);

          // addedCart above usually lacks item_count; deliver the now-resolved,
          // authoritative cart to listeners (e.g. header-actions.js's screen-reader
          // announcement) that need a real count and can't fall back like cart-icon.js does.
          if (cart) {
            this.dispatchEvent(
              new CartUpdateEvent(cart, id.toString(), {
                source: 'product-form-component',
                productId: this.dataset.productId,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        console.error(error);
        this.#resetAddToCartAnimation();

        this.dispatchEvent(
          new CartErrorEvent(
            form.getAttribute('id') || '',
            Theme.translations?.cart_error || 'Unable to add this item to your bag. Please try again.'
          )
        );

        this.#syncAddToCartButtonState();
      })
      .finally(() => {
        this.#setAddToCartBusy(false);

        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: cartItemComponentsSectionIds.join(','),
      sections_url: buildCartSectionsUrl(),
    };

    const animationPromise = this.#startAddToCartAnimation();
    this.#setAddToCartBusy(true);

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.#cartAddBlocked = true;
          this.#resetAddToCartAnimation();

          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );

          await this.#fetchAndUpdateCartQuantity();
          this.#syncAddToCartButtonState();

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(response.message));
            }
            this.#setLiveRegionText(response.message);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );
          return;
        }

        this.#cartAddBlocked = false;

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const cartWasEmpty = this.#cartAppearsEmpty();
        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

        // See #processAddToCart: don't block the drawer/panel open + section morph
        // on the (often necessary) extra /cart.js round trip for an authoritative count.
        const addedCart = this.#cartFromAddResponse(response);
        const cartPromise = this.#resolveCartAfterAdd(response, addedCart);

        this.dispatchEvent(
          new CartAddEvent(addedCart ?? undefined, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );

        // See #processAddToCart: sequence the panel auto-open after the animation
        // finishes, without gating the eager morph/bubble sync on it.
        Promise.resolve(animationPromise).then(() => {
          this.dispatchEvent(
            new CartAddAnimationCompleteEvent(this.id, {
              source: 'product-form-component',
              productId: this.dataset.productId,
            })
          );
        });

        await this.#morphCartSections(response.sections, cartItemComponentsSectionIds, cartWasEmpty);

        // See #processAddToCart: prompt, unconditional signal that cart panel
        // content is populated — doesn't wait on the authoritative-count fetch.
        this.dispatchEvent(
          new CartSectionsMorphedEvent(this.id, { source: 'product-form-component' })
        );

        const cart = await cartPromise;
        this.#syncAddToCartButtonState();
        await this.#syncAllCartBubbles(cart);

        // See #processAddToCart: deliver the authoritative cart once resolved.
        if (cart) {
          this.dispatchEvent(
            new CartUpdateEvent(cart, this.id, {
              source: 'product-form-component',
              productId: this.dataset.productId,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
        this.#resetAddToCartAnimation();
      })
      .finally(() => {
        this.#setAddToCartBusy(false);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * Finds the matching product form in a section-rendering / full-page HTML response.
   * @param {Document | Element} html
   * @returns {Element | null}
   */
  #getResponseProductForm(html) {
    const { productId, sectionId } = this.dataset;
    if (!productId || !sectionId) return null;

    return html.querySelector(
      `product-form-component[data-product-id="${productId}"][data-section-id="${sectionId}"]`
    );
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    this.#cartAddBlocked = false;
    this.#variantChangeInProgress = false;

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    const responseForm = this.#getResponseProductForm(event.detail.data.html);

    const newAddToCartButton = responseForm?.querySelector('[ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton, {
        ...MORPH_OPTIONS,
        getNodeKey: (node) => {
          if (!(node instanceof HTMLElement)) return undefined;
          return node.dataset.key ?? node.id ?? undefined;
        },
      });
      currentAddToCartButtonContainer.cancelAnimation();
      this.#resetAddToCartButtonPresentation(currentAddToCartButton);
    }

    // Process any add-to-cart clicks queued while the variant was in flight —
    // after the button morph/cleanup above, so this starts a clean animation
    // cycle instead of one that gets immediately cancelled by it.
    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    this.#syncAddToCartButtonState(Boolean(event.detail.resource?.available));

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      responseForm?.querySelector('quantity-selector-component input[ref="quantityInput"]') ?? null
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = responseForm?.querySelector('.quantity-rules') ?? null;
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = responseForm?.querySelector('price-per-item') ?? null;
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = responseForm?.querySelector('.product-form-buttons') ?? null;

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          responseForm?.querySelector('quantity-selector-component input[ref="quantityInput"]') ?? null
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, responseForm?.querySelector(selector) ?? null, fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = responseForm?.querySelector('volume-pricing') ?? null;
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#addAbortController?.abort();
    this.#addAbortController = null;
    this.#addRequestId++;
    this.#variantChangeInProgress = true;
    this.#applyOptimisticVariantSelection();
  };

  /**
   * Enables or disables Add to bag immediately from the picker DOM — no fetch wait.
   */
  #applyOptimisticVariantSelection() {
    const picker = this.closest('.shopify-section')?.querySelector(
      'variant-picker[data-template-product-match="true"]'
    );

    if (!picker || typeof picker.resolveSelectedVariant !== 'function') return;

    const variant = picker.resolveSelectedVariant();
    const { variantId } = this.refs;

    if (variant?.id) {
      this.#cartAddBlocked = false;
      variantId.value = String(variant.id);

      if (this.refs.quantitySelector) {
        this.#fetchAndUpdateCartQuantity().then(() => {
          this.#syncAddToCartButtonState(variant.available);
        });
      } else {
        this.#syncAddToCartButtonState(variant.available);
      }
    } else {
      variantId.value = '';
      this.#variantCartQuantity = 0;
      this.#syncAddToCartButtonState(false);
    }
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getVariantPicker() {
    return this.closest('.shopify-section')?.querySelector(
      'variant-picker[data-template-product-match="true"]'
    );
  }

  /**
   * @returns {boolean}
   */
  #isAtCartQuantityLimit() {
    if (this.#cartAddBlocked) return true;

    const quantityToAdd = this.#getQuantity();

    if (this.refs.quantitySelector?.canAddToCart) {
      return !this.refs.quantitySelector.canAddToCart().canAdd;
    }

    const picker = this.#getVariantPicker();
    if (typeof picker?.canAddVariantToCart === 'function') {
      return !picker.canAddVariantToCart(this.#variantCartQuantity, quantityToAdd).canAdd;
    }

    return false;
  }

  /**
   * @param {number | null | undefined} [hint]
   * @returns {number | null}
   */
  #resolveMaxQuantity(hint) {
    if (typeof hint === 'number' && hint > 0) return hint;

    const picker = this.#getVariantPicker();
    if (typeof picker?.canAddVariantToCart === 'function') {
      const { maxQuantity } = picker.canAddVariantToCart(this.#variantCartQuantity, this.#getQuantity());
      if (typeof maxQuantity === 'number' && maxQuantity > 0) return maxQuantity;
    }

    if (this.refs.quantitySelector?.canAddToCart) {
      const { maxQuantity } = this.refs.quantitySelector.canAddToCart();
      if (typeof maxQuantity === 'number' && maxQuantity > 0) return maxQuantity;
    }

    const quantityInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('quantity-selector-component input[ref="quantityInput"], input[name="quantity"]')
    );
    if (quantityInput?.max) {
      const parsedMax = parseInt(quantityInput.max, 10);
      if (!Number.isNaN(parsedMax) && parsedMax > 0) return parsedMax;
    }

    if (this.#variantCartQuantity > 0) return this.#variantCartQuantity;

    return null;
  }

  /**
   * @param {number | null | undefined} [maxQuantity]
   * @returns {string}
   */
  #formatMaxQuantityErrorMessage(maxQuantity) {
    const resolvedMax = this.#resolveMaxQuantity(maxQuantity);

    if (resolvedMax != null) {
      const template = this.dataset.quantityErrorMax || 'This item has a maximum of {{ maximum }}';
      return template.replace('{{ maximum }}', String(resolvedMax));
    }

    if (this.#variantCartQuantity > 0) {
      const template = this.dataset.quantityOnlyAvailable || 'Only {{ count }} available';
      return template.replace('{{ count }}', String(this.#variantCartQuantity));
    }

    return 'You cannot add more of this item to your cart.';
  }

  /**
   * @param {string} errorMessage
   */
  #displayAddToCartError(errorMessage) {
    const { addToCartTextError } = this.refs;
    if (!addToCartTextError || !errorMessage) return;

    addToCartTextError.classList.remove('hidden');

    const textNode = addToCartTextError.childNodes[2];
    if (textNode) {
      textNode.textContent = errorMessage;
    } else {
      addToCartTextError.appendChild(document.createTextNode(errorMessage));
    }

    this.#setLiveRegionText(errorMessage);

    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      if (!addToCartTextError) return;
      addToCartTextError.classList.add('hidden');
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  /**
   * @param {number | null | undefined} [maxQuantity]
   */
  #showMaxQuantityError(maxQuantity) {
    this.#displayAddToCartError(this.#formatMaxQuantityErrorMessage(maxQuantity));
    this.#syncAddToCartButtonState();
  }

  /**
   * Clears add-to-cart animation state after morphing so label text stays visible.
   * @param {HTMLButtonElement} button
   */
  #resetAddToCartButtonPresentation(button) {
    button.removeAttribute('data-added');

    const addToCartText = button.querySelector('.add-to-cart-text');
    if (addToCartText instanceof HTMLElement) {
      addToCartText.style.removeProperty('clip-path');
      addToCartText.style.removeProperty('opacity');
      addToCartText.style.removeProperty('filter');
      addToCartText.style.removeProperty('translate');
      addToCartText.style.removeProperty('width');
    }
  }

  /**
   * Disables add-to-cart when no variant id is set (PDP requires explicit selection).
   * @param {boolean} [variantAvailable=true]
   */
  #syncAddToCartButtonState(variantAvailable = true) {
    const variantId = this.refs.variantId?.value?.trim();
    const shouldEnable = Boolean(variantId) && variantAvailable && !this.#isAtCartQuantityLimit();
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    for (const container of allAddToCartContainers) {
      if (shouldEnable) {
        container.enable();
      } else {
        container.disable();
      }
    }
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
